import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { AuthenticationType } from '~src/settings/types/IServerSettingsUnauthenticated';
import { getLinkMarkup, ILink } from '~src/spacesPlatform/components/ListView/dataGridCells/LinkCell';
import { PlainTextCellWithIcon } from '~src/spacesPlatform/components/ListView/dataGridCells/PlainTextAndNumberCells';
import { StyledCellFormatter } from '~src/spacesPlatform/components/ListView/StyledCellWrapper';

import { Messages } from '../../../../codegen/Localize';
import { SiteRolesHelper } from '../../../../siteManagement/helpers/SiteRolesHelper';
import { ProfilePictureStyles } from '../../../components/styles/ProfilePictureStyles';
import { DataGridProfilePicture } from '../../../components/userProfilePicture/DataGridProfilePicture';
import { ISiteUserInfo } from '../../models/ISiteUserInfo';
import { IUserInfo } from '../../models/IUserInfo';
import { SiteUserAuthenticationProviderContext } from './UserAuthenticationProviderContext';

export namespace UserDataGridCells {

  export function SiteRoleCell(testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(function SiteRoleCell(props: BodyCell.Props) {
      const user = props.row.data as ISiteUserInfo;
      const text = SiteRolesHelper.siteRoleToDisplayName(user.siteRole, user.userCategory);
      return <span title={text} data-tb-test-id={testId}>{text}</span>;
    });
  }

  export function ProfilePictureCell(
      getProfileProps: (row: DataGrid.Row) => DataGridProfilePicture.Props,
      getLinkOptions: (row: DataGrid.Row) => ILink
    ): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(function ProfilePictureCells(props: BodyCell.Props) {
      const linkOptions = getLinkOptions(props.row);
      return (
        <div style={ProfilePictureStyles.Layout.UserNameWithProfilePictureCellWrapperStyle} data-tb-test-id='user-profile-photo'>
          <span style={ProfilePictureStyles.Layout.UserNameWithProfilePictureCellChildStyle}>
            <DataGridProfilePicture {...getProfileProps(props.row)} />
          </span>
          <span style={ProfilePictureStyles.Layout.UserNameWithProfilePictureCellChildDisplayNameStyle}>
            <span data-tb-test-id={linkOptions.testId} style={CommonStyles.Layout.FlexBlockLeftAndCenter}>
              {getLinkMarkup(linkOptions, props.row)}
            </span>
          </span>
        </div>
      );
    });
  }

  export function AuthenticationProviderConsumerHOC(Component: any): React.StatelessComponent<BodyCell.Props> {
    return (props: BodyCell.Props) =>
      <SiteUserAuthenticationProviderContext.Consumer>
        {provider => Component({getUserAuthenticationInfo: provider})(props)}
      </SiteUserAuthenticationProviderContext.Consumer>;
  }

  export interface AuthenticationProps {
    readonly getUserAuthenticationInfo: (user: ISiteUserInfo) => {name: string, isEnabled: boolean};
  }
  export function AuthenticationProviderConsumer(authProps: AuthenticationProps) {
    return function AuthenticationProviderConsumer(props: BodyCell.Props) {
      const metadata = authProps.getUserAuthenticationInfo(props.row.data as ISiteUserInfo);
      return PlainTextCellWithIcon((row: any) => {
        return {
          text: metadata.name,
          iconClass: metadata.isEnabled ? null : 'tb-icon-alert-list-header tb-react-dg-hcol',
          iconTitle: Messages.disabled()
        };
      })(props);
    };
  }

  export const AuthenticationCell = AuthenticationProviderConsumerHOC(AuthenticationProviderConsumer);

  export function IdentityPoolsCell(testId?: string):
  React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
        function IdentityPoolsCell(props: BodyCell.Props) {
          const user = props.row.data as IUserInfo;
          let poolMemberships = user.poolMemberships;
          let result = [];
          if (poolMemberships && poolMemberships.length > 0) {
            for (let i = 0; i < poolMemberships.length; i ++) {
              if (poolMemberships[i].identityPool) {
                const pool = poolMemberships[i].identityPool;
                const poolIdentityStore = pool.identityStoreType.toUpperCase() === 'LOCAL' ? Messages.localDomain() : pool.identityStoreType;
                const tooltip = Messages.identityStore() + ' = ' + poolIdentityStore + ' | ' + Messages.authentication() + ' = ' + authTypeHelper(pool.authType);
                const text = (i === poolMemberships.length - 1) ? pool.name : pool.name + ', ';
                const asset = <span title={tooltip} data-tb-test-id={testId} key={i}> {text} </span>;
                result.push(asset);
              }
            }
            return (<div>{ result }</div>);
          }
          return <span title={Messages.identityPool_None_User()} data-tb-test-id={testId}> {Messages.identityPool_None()} </span>;
      });
  }

  function authTypeHelper(authType: AuthenticationType) {
    switch (authType) {
      case AuthenticationType.Local:
        return Messages.localDomain();
      case AuthenticationType.ClientCertificate:
        return Messages.authenticationType_clientCertificate();
      case AuthenticationType.ClientCertificateWithFallback:
        return Messages.authenticationType_clientCertificateWithFallback();
      case AuthenticationType.External:
        return Messages.externalDomain();
      default:
        return authType;
    }
  }
}
